import { Deserializer } from 'jsonapi-serializer';
import { NO_CONTENT } from '../constants/response-status-codes';
import { Floor, FloorWrapper } from '../models/Floor';
import { genericError } from '../models/GenericError';
import { ListQuery } from '../models/ListQuery';
import { Name } from '../models/Name';
import { PaginationModel } from '../models/Pagination';
import { StoreEntityResult } from '../models/services/StoreEntityResult';
import { APIConnector } from './api/APIConnector';
import { createPagination } from './utilities/paginationBuilder';
import { processServerError, getConstraintViolation } from './utilities/ServiceError';
import { createPartialURL, getFilterClause } from './utilities/urlBuilder';

const apiConnector = new APIConnector();

const buildFloorData = (floor): Floor => {
    return {
        id: floor['id'],
        building: floor['building']['building-name'],
        level: floor['floor-level'],
        name: floor['floor-name'],
        site: floor['building']['site']['site-name'],
        default: floor['floor-default'],
        isDefault: floor['is-default']
    }
}

export const getFloorsForPerson = async (masterId: number, listParameters?: ListQuery): Promise<FloorWrapper> => {
    try {
        let filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        filter = (filter === '' ? `&filter=master.id==${masterId}` : `${filter};master.id==${masterId}`);
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const url = `masterFloor?include=floor.building.site${partialURL}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, floors: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const personFloors: Floor[] = floors.map(buildFloorData);
                return resolve({ rows: personFloors, pagination });
            })

        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getFloors = async (listParameters?: ListQuery): Promise<FloorWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const floorFields = 'fields[floor]=building,floorLevel,floorName,floorDefault,isDefault';
        const buildingFields = 'fields[building]=buildingName,site';
        const siteFields = 'fields[site]=siteName';
        const url = `floor?include=building.site${partialURL}&${floorFields}&${buildingFields}&${siteFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, floors: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const buildingFloors: Floor[] = floors.map(buildFloorData);
                return resolve({ rows: buildingFloors, pagination });
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getFloorById = async (id: string): Promise<Floor> => {
    try {
        const url = `floor/${id}?include=building.site`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail), success: false })
            }

            new Deserializer().deserialize(json, (err: any, response: any) => {
                if (err) {
                    return reject(err);
                }
                const floor: Floor = {
                    id: response['id'],
                    level: response['floor-level'],
                    name: response['floor-name'],
                    inputs: response['total-inputs'],
                    site: response['building']['site']['site-name'],
                    building: response['building']['building-name'],
                    buildingId: response['building']['id'],
                    default: response['floor-default'],
                    description: response['floor-desc']
                }
                return resolve(floor);
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const deleteFloor = async (id: string): Promise<{ id: string, success: boolean }> => {
    try {
        const url = `floor/${id}`;
        const floor = {
            data: {
                type: "floor",
                id: id
            }
        }
        const response = await apiConnector.deleteData(url, floor)

        return new Promise(async (resolve, reject) => {
            if (response.status === 204) {
                return resolve({ id: id, success: true })
            }

            const json = await response.json();
            return reject({ status: response.status, id: id, success: false, message: getConstraintViolation(json.errors[0].detail) });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const assignFloor = async (floor: Floor, createFloor = true): Promise<StoreEntityResult> => {
    try {
        const newRecord = {
            data: {
                type: 'floor',
                id: floor.id,
                attributes: {
                    floorDefault: createFloor ? 0 : floor.default,
                    floorDesc: floor.description,
                    floorLevel: floor.level,
                    floorName: floor.name
                },
                relationships: {
                    building: {
                        data: {
                            type: 'building',
                            id: floor.buildingId
                        }
                    }
                }
            }
        }

        const url = `floor${createFloor ? '' : `/${newRecord.data.id}`}`;
        let response;

        await apiConnector.sendData(url, createFloor, newRecord).then(data => {
            response = data;
        }).catch(error => {
            console.log('Save floor error: ', error);
        });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (response.status === NO_CONTENT) {
                    return resolve({ id: floor.id, success: true })
                }

                const content = await response.json();

                if (content.errors) {
                    return reject({ status: response.status, message: getConstraintViolation(content.errors[0].detail) })
                }

                new Deserializer().deserialize(content, (error, newFloor) => {
                    if (error) {
                        return reject({ status: response.status, id: floor.id, success: false });
                    }

                    return resolve({ id: newFloor.id, success: true });
                });

                return;
            }

            return reject({ status: response.status, success: false, message: genericError });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getFloorsNamesWithPagination = async (listQuery?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filters: string = getFilterClause(listQuery?.filters, listQuery?.searchTerm);
        const paginationString = createPartialURL(listQuery?.goToPage, listQuery?.pageSize, listQuery?.sortBy, filters);
        const url = `floor?fields[floor]=floorName,isDefault&${paginationString}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, items: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const floors: Name[] = items.map((floor): Name => {
                    return {
                        value: floor['id'],
                        label: floor['floor-name'],
                        isDefault: floor['is-default']
                    };
                });

                return resolve({ rows: floors, pagination: newPagination });
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const autoCreateFloorsForBuilding = async (buildingId: string, startLevel: number, endLevel: number): Promise<StoreEntityResult> => {
    try {
        const requestBody = {
            type: "autoCreateFloors",
            status: "",
            attributes: {
                floorLevelStart: startLevel,
                floorLevelEnd: endLevel
            },
            relationships: {
                buildingId: buildingId
            }
        }

        const response: any = await apiConnector.sendData('', true, requestBody, true);
        const json = await response.json();
        
        return new Promise((resolve, reject) => {

            if (json && json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            if (response) {
                if (response.errors) {
                    return reject({ status: response.status, message: getConstraintViolation(response.errors[0].detail) })
                }

                return resolve({ id: 0, success: true });
            }

            return reject({ status: response.status, message: genericError });
            
        });

    } catch (error) {
        return Promise.reject(processServerError(error));
    }
} 

export const getFloorTabCounts = async (floorId: string): Promise<{ floorGroupCount: number, liftCount: number }> => {
    try {
        const floorFields = 'fields[floor]=totalFloorGroups,totalLifts';
        const url = `floor/${floorId}?${floorFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({
                    status: response.status,
                    message: getConstraintViolation(json.errors[0].detail),
                });
            }

            new Deserializer().deserialize(json, (err: any, data: any) => {
                if (err) {
                    return reject({
                        status: response.status,
                        message: genericError,
                    });
                }

                const counts = {
                    floorGroupCount: data['total-floor-groups'],
                    liftCount: data['total-lifts']
                };
                resolve(counts);
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}